<template>
    <LayoutBase class="view-base">
        <div v-if="success" class="success">
            <div class="top-card">
                <img class="icon" src="@/assets/success.png" alt="" />
                <div class="title">{{ $t("registration.registered") }}</div>
            </div>
            <van-list class="list mt-small">
                <van-cell class="row" :title="$t('registration.orderNo')" :value="feeOrder.orderNo" />
                <van-cell class="row" :title="$t('registration.orderCreateAt')" :value="feeOrder.orderCreatedAt" />
            </van-list>
            <div class="attention" v-if="!isEmptySchoolQrCode">
                <div class="qr-bg">
                    <img :src="schoolQrCode" />
                </div>
            </div>
        </div>
        <div v-else-if="studentRegistration" class="confirm">
            <van-row>
                <van-col span="24">
                    <PlanCard :plan-item="item" :org-config="orgConfig" />
                </van-col>
            </van-row>
            <BasicInfoCell :info="item" :is-foreign="!item.isDomestic" :org-config="orgConfig"></BasicInfoCell>
            <IdentityInfoCell
                :info="item"
                :is-foreign="!item.isDomestic"
                :id-card-image="idCardImage"
                :id-card-back-image="idCardBackImage"
                :id-photo-image="idPhotoImage"
                :org-config="orgConfig"
                :class="{
                    'is-last-card': !(
                        showNationalityType &&
                        !item.isInternalUser &&
                        (newRegistrationInfoEnabled || !item.isDomestic)
                    )
                }"
            ></IdentityInfoCell>
            <RegistrationInfoCell
                v-if="showNationalityType && !item.isInternalUser && (newRegistrationInfoEnabled || !item.isDomestic)"
                class="identity-info-card is-last-card"
                :info="item"
                :is-foreign="!item.isDomestic"
                :read-only="!item.isAllowRegistration"
            ></RegistrationInfoCell>
            <van-tabbar class="payable-container">
                <van-tabbar-item class="payable-fee-container">
                    <span class="payable" v-if="isPaySuccess">{{ $t("plan.paid") }}:</span>
                    <span class="payable" v-if="isRefundSuccess">{{ $t("plan.refund") }}:</span>
                    <span class="payable" v-if="!isPaySuccess && !isRefundSuccess">{{ $t("plan.payable") }}:</span>
                    <span class="payable-fee">{{ item.registrationAmount }}</span>
                </van-tabbar-item>
                <van-tabbar-item class="payment-button-container">
                    <van-button
                        block
                        class="payable-button online-payment-button"
                        :class="{ disabled: !canPayOrder || !isCompleteRegistrationInfo }"
                        :loading="loading"
                        @click="doPayment"
                        v-if="
                            isOnlinePayPlan &&
                                !isPaySuccess &&
                                !isRefundSuccess &&
                                canPayOrder &&
                                item.isAllowRegistration
                        "
                    >
                        <span class="payment-container">{{ $t("plan.payment") }}</span>
                    </van-button>
                    <van-button
                        block
                        class="payable-button btn-preview"
                        :loading="loading"
                        @click="doSubmit"
                        v-if="
                            !isOnlinePayPlan && !isPaySuccess && !isRefundSuccess && item.registrationInfo !== undefined
                        "
                        :class="{
                            disabled:
                                (showNationalityType && !item.isInternalUser) ||
                                (item.isInternalUser && hasRegistrationInfo)
                        }"
                    >
                        <span v-if="!hasRegistrationInfo">
                            {{ $t("registration.title") }}
                        </span>
                        <span v-if="hasRegistrationInfo">{{ $t("registration.registered") }}</span>
                    </van-button>
                    <van-button
                        @click="goBack"
                        block
                        class="payable-button online-payment-button disabled"
                        v-if="isPaySuccess || isRefundSuccess"
                    >
                        <span class="payment-container" v-if="isPaySuccess">{{ $t("common.btn.paid") }}</span>
                        <span class="payment-container" v-if="isRefundSuccess">{{ $t("common.btn.refund") }}</span>
                    </van-button>
                </van-tabbar-item>
            </van-tabbar>
        </div>
    </LayoutBase>
</template>

<script>
import _ from "lodash";
import Vue from "vue";
import { feeOrder, registration, registrationInfo, studentRegistrationInfo } from "@/api/plan";
import { datetimeFormat, emptyValue, enumI18nFormat, getCentAmount, moneyFormat } from "@/utils/lang";
import PlanCard from "@/views/plan/DetailPlanCard.vue";
import { $constants } from "@/scripts/constants";
import { Toast } from "vant";
import config from "@/config";
import BasicInfoCell from "@/views/my/info/BasicInfoCell.vue";
import IdentityInfoCell from "@/views/my/info/IdentityInfoCell.vue";
import RegistrationInfoCell from "@/views/plan/RegistrationInfo.vue";
import { createNamespacedHelpers } from "vuex";
import { getOrderPaymentUrl } from "@/api/onlinePayment";

const { mapGetters, mapActions } = createNamespacedHelpers("config");

const ItemMapper = (registration, info) => {
    const planItem = {
        id: _.get(registration, "plan.id", 0),
        orgName: _.get(registration, "plan.org.name", ""),
        planName: _.get(registration, "plan.planName", ""),
        isAllowRegistration: _.get(registration, "plan.allowRegistration", false),
        registrationCount: _.get(registration, "plan.registrationCount", "0"),
        levelName: enumI18nFormat("levelEnum", _.get(registration, "plan.program.occupationInfo.occupationLevel")),
        level: _.get(registration, "plan.program.occupationInfo.occupationLevel", ""),
        occupationName: _.get(registration, "plan.program.occupationInfo.occupationName", ""),
        classificationName: _.get(registration, "plan.program.occupationInfo.classificationName", ""),
        timeRange:
            datetimeFormat(_.get(registration, "plan.setting.startTime"), $constants.DATE_FMT) +
            " - " +
            datetimeFormat(_.get(registration, "plan.setting.endTime"), $constants.DATE_FMT),
        registrationAmount: moneyFormat(_.get(registration, "plan.setting.fee", 0)),
        registrationCentAmount: getCentAmount(_.get(registration, "plan.setting.fee", 0)),
        registrationEnrollmentCount:
            _.get(registration, "plan.registrationCount") + "/" + _.get(registration, "plan.setting.enrollmentCount"),
        registrationStatus: _.get(registration, "plan.setting.registrationStatus"),
        certificateType: _.get(registration, "plan.setting.certificateType"),
        coverPath: _.get(registration, "plan.introduction.cover.thumbnailUrl", ""),
        studentName: emptyValue(_.get(registration, "student.user.name")),
        identityType: _.get(registration, "student.identityType"),
        identityNo: emptyValue(_.get(registration, "student.identityNo")),
        isDomestic: _.get(registration, "student.user.isDomestic", true),
        idPhoto: _.get(registration, "student.idPhoto", {}),
        idCardFront: _.get(registration, "student.idCardFront", {}),
        idCardBack: _.get(registration, "student.idCardBack", {}),
        enableOnlinePayment: _.get(registration, "plan.setting.enableOnlinePayment", "NO"),
        expectedOpeningTime: emptyValue(datetimeFormat(_.get(registration, "plan.setting.openClassAt"))),
        trainingType: enumI18nFormat("trainTypeEnum", _.get(registration, "plan.setting.trainingType")),
        registrationInfo: info
    };
    return {
        ..._.get(registration, "student.user", {}),
        ..._.get(registration, "student.userAttributes", {}),
        ...planItem
    };
};

const OrderMapper = feeOrder => {
    return {
        orderNo: _.get(feeOrder, "orderNo", ""),
        orderCreatedAt: datetimeFormat(_.get(feeOrder, "createdAt"))
    };
};

export default Vue.extend({
    name: "PlanDetail",
    components: { RegistrationInfoCell, IdentityInfoCell, BasicInfoCell, PlanCard },
    props: {
        planId: {
            type: [Number]
        }
    },
    data() {
        return {
            loading: false,
            success: false,
            order: null,
            studentRegistration: null,
            registration: null,
            schoolQrCode: config.SCHOOL_QR_CODE,
            item: {},
            DefaultCover: require("@/assets/plan-default-cover.png")
        };
    },
    computed: {
        isCompleteRegistrationInfo() {
            if (
                this.showNationalityType &&
                // this.zhixiaoPaymentEnabled &&
                // this.onlinePaymentEnabled &&
                this.item.registrationCentAmount > 0
            ) {
                if (this.item.isInternalUser) {
                    return true;
                }
                const registrationInfo = this.item.registrationInfo;
                let commonCanPayOrder = true;
                commonCanPayOrder =
                    registrationInfo.highestEducation &&
                    registrationInfo.healthStatus &&
                    registrationInfo.attachments.length > 0;
                let foreignCanPayOrder = true;
                if (!this.item.isDomestic) {
                    foreignCanPayOrder =
                        registrationInfo.chineseLevel &&
                        registrationInfo.company &&
                        registrationInfo.fax &&
                        registrationInfo.address &&
                        registrationInfo.contactPhone &&
                        registrationInfo.guarantor &&
                        registrationInfo.guarantorPhone &&
                        registrationInfo.contacts.length > 0;
                    let foreignNewRegistrationInfoCanPayOrder = true;
                    if (this.newRegistrationInfoEnabled) {
                        foreignNewRegistrationInfoCanPayOrder =
                            registrationInfo.recommendingCompany &&
                            registrationInfo.recommendingCompanyPhone &&
                            registrationInfo.major &&
                            registrationInfo.studentType &&
                            registrationInfo.studyStartDate &&
                            registrationInfo.studyEndDate &&
                            registrationInfo.fundingSource;
                    }
                    foreignCanPayOrder = foreignCanPayOrder && foreignNewRegistrationInfoCanPayOrder;
                }
                let newRegistrationInfoCanPayOrder = true;
                if (this.newRegistrationInfoEnabled) {
                    newRegistrationInfoCanPayOrder = registrationInfo.tcmBackground && registrationInfo.studyPlan;
                }
                return !!commonCanPayOrder && !!newRegistrationInfoCanPayOrder && !!foreignCanPayOrder;
            }
            return true;
        },
        ...mapGetters(["orgConfig", "onlinePaymentConfig"]),
        feeOrder() {
            return OrderMapper(this.order);
        },
        isEmptySchoolQrCode() {
            return _.isEmpty(config.SCHOOL_QR_CODE);
        },
        idPhotoImage() {
            return this.getImageSrc(this.item.idPhoto, "id-photo-default");
        },
        idCardImage() {
            return this.getImageSrc(this.item.idCardFront, "default-identity-img");
        },
        idCardBackImage() {
            return this.getImageSrc(this.item.idCardBack, "default-identity-back-img");
        },
        showNationalityType() {
            if (_.keys(this.orgConfig).length == 0) {
                return false;
            }
            return !!this.orgConfig["showNationalityType"];
        },
        newRegistrationInfoEnabled() {
            if (_.keys(this.orgConfig).length == 0) {
                return false;
            }
            return !!this.orgConfig["newRegistrationInfoEnabled"];
        },
        zhixiaoPaymentEnabled() {
            if (_.keys(this.onlinePaymentConfig).length == 0) {
                return false;
            }
            return !!this.onlinePaymentConfig["enabled"];
        },
        onlinePaymentEnabled() {
            if (_.keys(this.orgConfig).length == 0) {
                return false;
            }
            return !!this.orgConfig["onlinePaymentEnabled"];
        },
        hasRegistrationInfo() {
            return !!this.item.registrationInfo && _.keys(this.item.registrationInfo).length !== 0;
        },
        isOnlinePayPlan() {
            return (
                this.item.enableOnlinePayment === "YES" &&
                this.zhixiaoPaymentEnabled &&
                this.onlinePaymentEnabled &&
                this.item.registrationCentAmount
            );
        },
        canPayOrder() {
            if (this.item.isInternalUser) {
                if (this.isCompleteRegistrationInfo && this.isOnlinePayPlan) {
                    if (this.registration.paymentStatus !== undefined) {
                        return this.registration.paymentStatus === "PAY_PENDING";
                    }
                    return true;
                }
            }
            if (this.isOnlinePayPlan) {
                if (this.registration.paymentStatus === undefined) {
                    return true;
                }
                return this.registration.paymentStatus === "PAY_PENDING";
            }
            return false;
        },
        isPaySuccess() {
            if (this.registration) {
                return this.registration.paymentStatus === "PAY_SUCCESS";
            }
            return false;
        },
        isRefundSuccess() {
            if (this.registration) {
                return this.registration.paymentStatus === "REFUND_SUCCESS";
            }
            return false;
        }
    },
    async mounted() {
        studentRegistrationInfo(this.planId)
            .then(registration => {
                this.studentRegistration = registration;
            })
            .catch(() => {
                this.$router.back();
            });
        this.registration = await registrationInfo(this.planId);
        await this.getOrgConfig();
        await this.getOnlinePaymentConfig();
        this.item = ItemMapper(this.studentRegistration, this.registration);
        this.item.coverPath = this.item.coverPath ? this.item.coverPath : this.DefaultCover;
    },
    methods: {
        ...mapActions(["getOrgConfig", "getOnlinePaymentConfig"]),
        getImageSrc(image, defaultImg) {
            if (_.isEmpty(image)) {
                return require(`@/assets/${defaultImg}.png`);
            } else {
                return image["downloadUrl"];
            }
        },
        toastError(errors) {
            _.map(errors, error => {
                Toast(_.get(error, "message", this.$t("common.error.unknown")));
            });
        },
        async doPayment() {
            if (!this.canPayOrder || !this.isCompleteRegistrationInfo) {
                return;
            }
            if (this.registration.paymentStatus === undefined) {
                await registration(this.planId);
            }
            const order = await feeOrder(this.planId);
            const orderInfo = await getOrderPaymentUrl({
                orderId: order.orderId,
                planId: this.planId
                // returnUrl: `https://tms-qa-h5.tronclass.com.cn/`
            });
            window.location.href = orderInfo.data.data.url;
        },
        goBack() {
            return (window.location.href = `/plan/${this.planId}`);
        },
        doSubmit() {
            this.loading = true;
            if (
                this.showNationalityType &&
                !this.item.isInternalUser &&
                (this.newRegistrationInfoEnabled || !this.item.isDomestic)
            ) {
                this.loading = false;
                if (!this.hasRegistrationInfo) {
                    return;
                }
                return this.goBack();
            }
            if (this.item.isInternalUser && this.hasRegistrationInfo) {
                this.loading = false;
                return this.goBack();
            }
            registration(this.planId)
                .then(() => feeOrder(this.planId))
                .then(order => {
                    this.order = order;
                    this.success = true;
                })
                .catch(errors => {
                    this.toastError(errors);
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }
});
</script>

<style lang="less" scoped>
@import "../../styles/vars.less";

.row {
    justify-content: space-between;
}

.van-cell__title {
    white-space: nowrap;
    color: @label-color;
    flex: 1 1 auto;
}

.van-cell__value {
    color: @field-icon-color;
    flex: 1 1 auto;
}

/deep/ .van-cell-group--inset {
    margin: 0 8px;
}

.view-base {
    height: 100vh;
    background: linear-gradient(180deg, var(--primary-brand-color-lightened-3) 0%, #f6f7f9 39%);
    .confirm {
        gap: 8px;
        display: flex;
        flex-direction: column;
        padding-top: 16px;

        .is-last-card {
            margin-bottom: 80px;
        }

        .btn-preview {
            color: #ffffff;
            background-color: @primary-subject-color;
        }
        .payable-container {
            padding-bottom: 8px;
            padding-top: 8px;
            background: #fff;
            box-shadow: 0 3px 10px 0 rgba(38, 40, 51, 0.1), 0 0 1px 0 rgba(38, 40, 51, 0.1);
            .payable-button {
                width: 176px;
                &.online-payment-button {
                    border-radius: 8px;
                    background: #ff7800;
                }
                &.disabled {
                    opacity: 0.5;
                }
            }
            .payable-fee-container {
                display: flex;
                align-content: flex-start;
                flex-wrap: wrap;
                padding-left: 16px;
                .payable {
                    color: #3c3e46;
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 18px;
                }
                .payable-fee {
                    color: #ff7800;
                    font-size: 24px;
                    font-style: normal;
                    font-weight: 700;
                    line-height: 32px;
                }
            }
            .payment-button-container {
                margin-right: 8px;

                .payment-container {
                    color: #fff;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 24px;
                }
            }
        }
    }

    .success {
        .top-card {
            text-align: center;
            background-color: #ffffff;
            padding: 47px;

            .icon {
                display: block;
                margin-left: auto;
                margin-right: auto;
                border: 0;
                width: 120px;
                height: 120px;
            }

            .title {
                font-weight: bold;
                color: #3c3e46;
            }

            .message {
                font-size: 14px;
                color: #4d525c;
                white-space: pre-wrap;
                text-align: justify;
                word-wrap: break-word;
            }
        }
        .attention {
            margin-top: 8px;
            .qr-bg {
                width: 375px;
                height: 140px;
                background: url("../../assets/attention-bg.png") no-repeat center;
                background-size: contain;
                position: relative;

                img {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: 0;
                    margin: auto;
                    width: 96px;
                    height: 96px;
                    object-fit: contain;
                    padding: 22px 256px 22px 23px;
                }
            }
        }
    }
}
</style>
